import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import themeable from 'zignal-landscape/lib/themeable';
import Loading from 'zignal-landscape/lib/Loading';
import Banner from 'zignal-landscape/lib/Banner';
import Button from 'zignal-landscape/lib/Button';
import Checkbox from 'zignal-landscape/lib/Checkbox';
import Label from 'zignal-landscape/lib/Label';
import { Light } from 'themes';
import useFeatureFlag from 'helpers/useFeatureFlag';
import * as actions from './UserForm.ducks';
import {
  initialValues, validate, buildPayload, canEditRoles, canEditCampaigns,
} from './userFormRules';
import ProfileFields from './fields/ProfileFields';
import AdminAssignments from './fields/AdminAssignments';
import PreferencesFields from './fields/PreferencesFields';
import FeatureToggles from './fields/FeatureToggles';
import VerifyPhoneModal from './components/VerifyPhoneModal/VerifyPhoneModal';

export const SMS_FLAG = 'ZEN_3185_INBOX_sms_notifications';

const mapStateToProps = (state) => ({
  form: state.UserForm,
  currentUser: state.AppUser,
});

const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign({}, stateProps, dispatchProps, ownProps);

const trackLegacy = (name) => {
  if (typeof window !== 'undefined' && window.Vent && typeof window.Vent.trigger === 'function') { window.Vent.trigger(name); }
};

const UserForm = ({
  styles, form, currentUser,
  screen, userId, accountId, campaignId, campaigns = [], accounts = [],
  accountDetectEnabled = false, accountNarrativesEnabled = false, autoTranslationLangOptions = [],
  submitLabel = 'Save', showCancel = true, showCreateAnother = false, onCancel, onCreated,
  clearState, loadTimezones, loadUser, setUser, saveUser, verifyPhone, checkPhone, closeVerifyModal, clearBanner,
}) => {
  const smsEnabled = useFeatureFlag(SMS_FLAG);
  const isNew = screen === 'create' && !(form.user && form.user.id);
  const loadedUser = form.user;
  const currentUserReady = !!currentUser.userId;

  const [values, setValues] = useState(() => initialValues(null, { isNew: true, account: accountId, campaigns: campaignId ? [campaignId] : [] }));
  const keepRef = useRef(null);
  const pendingResetRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [phoneValid, setPhoneValid] = useState(null);
  const [createAnother, setCreateAnother] = useState(false);

  useEffect(() => {
    trackLegacy('view:page');
    clearState();
    loadTimezones();
    if (screen === 'edit' && userId) { loadUser(userId); }
    return () => { clearState(); };
  }, []);

  // My Settings is seeded from the bootstrap user once AppUser has hydrated.
  useEffect(() => {
    if (screen === 'mySettings' && currentUserReady && !form.user) { setUser(currentUser.user); }
  }, [screen, currentUserReady]);

  const reseed = () => {
    const kept = keepRef.current;
    setValues(initialValues(loadedUser, {
      isNew: !loadedUser,
      account: kept ? kept.account : accountId,
      campaigns: kept ? kept.campaigns : (campaignId ? [campaignId] : []),
    }));
    setErrors({});
    setPhoneValid(null);
  };

  // Re-seed the form whenever the stored user changes (load, save).
  useEffect(() => { reseed(); }, [loadedUser]);

  // Create-another leaves form.user null (it was already null), so the user
  // change above never fires. Reset on the save result instead; a failed save
  // keeps what was typed.
  useEffect(() => {
    if (!pendingResetRef.current || !form.banner) { return; }
    pendingResetRef.current = false;
    if (form.banner.type === 'success') { reseed(); }
  }, [form.banner]);

  // After a plain create, hand the new user to the screen (URL swap to Edit).
  useEffect(() => {
    if (screen === 'create' && form.user && form.user.id && onCreated) { onCreated(form.user); }
  }, [form.user]);

  const handleChange = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePhoneChange = useCallback((e164, isValid) => {
    setPhoneValid(isValid);
    setValues((prev) => ({ ...prev, phone: e164 }));
  }, []);

  const context = useMemo(() => ({
    screen, isNew, currentUser, loadedUser, campaigns, accounts, accountDetectEnabled, accountNarrativesEnabled, autoTranslationLangOptions,
  }), [screen, isNew, currentUser, loadedUser, campaigns, accounts, accountDetectEnabled, accountNarrativesEnabled, autoTranslationLangOptions]);

  const showVerification = smsEnabled && screen === 'mySettings';
  const loadedPhone = ((loadedUser && loadedUser.phone) || '').trim();
  const typedPhone = (values.phone || '').trim();
  // Once a create has saved, the form edits that user: Save, and no create-another.
  const label = isNew ? submitLabel : 'Save';
  const offerCreateAnother = showCreateAnother && isNew;

  const handleSubmit = (e) => {
    if (e && e.preventDefault) { e.preventDefault(); }
    const nextErrors = validate(values, {
      isNew,
      canEditRoles: screen !== 'mySettings' && canEditRoles(currentUser),
      canEditCampaigns: canEditCampaigns(currentUser),
      canEditType: currentUser.isInternalAdmin === true,
      phoneValid,
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) { return; }
    if (offerCreateAnother && createAnother) {
      keepRef.current = { account: values.account, campaigns: values.campaigns };
      pendingResetRef.current = true;
    }
    trackLegacy(isNew ? 'create_user:account_users' : 'edit_user:account_users');
    const payload = buildPayload(values, { screen, isNew, currentUser, loadedUser, campaigns });
    if (payload.password) { trackLegacy('change_password:account_users'); }
    // The payload leaves an unchanged phone out, so compare what was typed.
    const phoneChanged = showVerification && typedPhone !== '' && typedPhone !== loadedPhone;
    saveUser({
      payload, isNew, accountId, screen, phoneChanged,
      previousVerified: form.verify.verified === true && typedPhone === loadedPhone,
      createAnother: offerCreateAnother && createAnother,
    });
  };

  if (!currentUserReady || form.isLoading || (screen !== 'create' && !loadedUser && !form.loadError)) {
    return <ThemeProvider theme={Light}><div className={styles.UserForm}><div data-cy="user-form-loading"><Loading /></div></div></ThemeProvider>;
  }

  if (form.loadError) {
    return (
      <ThemeProvider theme={Light}>
        <div className={styles.UserForm} data-cy="user-form-load-error">
          <Banner bannerStyle="inline" bannerType="error">{form.loadError}</Banner>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={Light}>
      <form className={styles.UserForm} data-cy="user-form" onSubmit={handleSubmit} noValidate>
        {form.banner && (
          <div data-cy="user-form-banner" onClick={clearBanner}>
            <Banner bannerStyle="inline" bannerType={form.banner.type === 'error' ? 'error' : 'success'} onClick={clearBanner}>
              {form.banner.message}
            </Banner>
          </div>
        )}
        <ProfileFields
          values={values} errors={errors} onChange={handleChange} onPhoneChange={handlePhoneChange}
          isNew={isNew} timezones={form.timezones}
          showVerification={showVerification} verified={form.verify.verified === true}
          phoneIsSaved={typedPhone === loadedPhone}
          isVerifying={form.verify.isSending === true}
          verifyError={form.verify.isModalOpen ? null : form.verify.error}
          onVerifyNow={() => verifyPhone(loadedPhone)}
        />
        <AdminAssignments values={values} errors={errors} onChange={handleChange} context={context} />
        <PreferencesFields values={values} onChange={handleChange} context={context} />
        <FeatureToggles values={values} onChange={handleChange} context={context} />

        <div className={styles.actions}>
          <Button type="submit" buttonStyle="positive" data-cy="user-form-save" disabled={form.isSaving} onClick={handleSubmit}>
            {form.isSaving ? `${label}...` : label}
          </Button>
          {showCancel && (
            <Button buttonStyle="secondary" data-cy="user-form-cancel" onClick={(e) => { e.preventDefault(); onCancel(); }}>Cancel</Button>
          )}
          {offerCreateAnother && (
            <span className={styles.createAnother}>
              <Checkbox id="user-form-create-another" name="create-another-user" checked={createAnother} onChange={(e) => setCreateAnother(e.target.checked)} />
              <Label htmlFor="user-form-create-another" inline>Create another</Label>
            </span>
          )}
        </div>

        {showVerification && (
          <VerifyPhoneModal
            isOpen={form.verify.isModalOpen}
            phone={form.verify.phone || loadedPhone}
            isSending={form.verify.isSending}
            isChecking={form.verify.isChecking}
            error={form.verify.error}
            onSubmitCode={(code) => checkPhone({ phone: form.verify.phone || loadedPhone, code })}
            onResend={() => verifyPhone(form.verify.phone || loadedPhone)}
            onClose={closeVerifyModal}
          />
        )}
      </form>
    </ThemeProvider>
  );
};

UserForm.propTypes = {
  styles: PropTypes.object,
  form: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  screen: PropTypes.oneOf(['mySettings', 'edit', 'create']).isRequired,
  userId: PropTypes.string,
  accountId: PropTypes.string,
  campaignId: PropTypes.string,
  campaigns: PropTypes.array,
  accounts: PropTypes.array,
  accountDetectEnabled: PropTypes.bool,
  accountNarrativesEnabled: PropTypes.bool,
  autoTranslationLangOptions: PropTypes.array,
  submitLabel: PropTypes.string,
  showCancel: PropTypes.bool,
  showCreateAnother: PropTypes.bool,
  onCancel: PropTypes.func,
  onCreated: PropTypes.func,
  clearState: PropTypes.func.isRequired,
  loadTimezones: PropTypes.func.isRequired,
  loadUser: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  saveUser: PropTypes.func.isRequired,
  verifyPhone: PropTypes.func.isRequired,
  checkPhone: PropTypes.func.isRequired,
  closeVerifyModal: PropTypes.func.isRequired,
  clearBanner: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, actions, mergeProps)(themeable(UserForm));
